import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { consultationsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import COUNTRIES from '@/data/countries';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    age: '',
    current_country: '',
    destination_country: '',
    request_type: 'تحصیلی',
    education_level: '',
    field_of_study: '',
    language_level: '',
    budget: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await consultationsAPI.create(formData);
      toast.success('درخواست شما با موفقیت ثبت شد. به زودی با شما تماس خواهیم گرفت.');
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        age: '',
        current_country: '',
        destination_country: '',
        request_type: 'تحصیلی',
        education_level: '',
        field_of_study: '',
        language_level: '',
        budget: '',
        description: '',
      });
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen py-12" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="page-title">درخواست مشاوره رایگان</h1>
          <p className="text-zinc-400">فرم زیر را تکمیل کنید تا مشاوران ما به زودی با شما تماس بگیرند</p>
        </div>

        <form onSubmit={handleSubmit} className="glass-effect border border-white/10 rounded-2xl p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">نام و نام خانوادگی *</label>
              <input
                type="text"
                name="full_name"
                required
                data-testid="full-name-input"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">شماره تماس *</label>
              <input
                type="tel"
                name="phone"
                required
                data-testid="phone-input"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">ایمیل *</label>
              <input
                type="email"
                name="email"
                required
                data-testid="email-input"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">سن *</label>
              <input
                type="text"
                name="age"
                required
                data-testid="age-input"
                value={formData.age}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">کشور محل اقامت *</label>
              <select
                name="current_country"
                required
                data-testid="current-country-input"
                value={formData.current_country}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 cursor-pointer"
              >
                <option value="" className="bg-[#1a1a1a] text-zinc-400">انتخاب کنید...</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className="bg-[#1a1a1a] text-white py-2">
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">کشور موردنظر *</label>
              <select
                name="destination_country"
                required
                data-testid="destination-country-input"
                value={formData.destination_country}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 cursor-pointer"
              >
                <option value="" className="bg-[#1a1a1a] text-zinc-400">انتخاب کنید...</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className="bg-[#1a1a1a] text-white py-2">
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">نوع درخواست *</label>
              <select
                name="request_type"
                required
                data-testid="request-type-select"
                value={formData.request_type}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 cursor-pointer"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)'
                }}
              >
                <option value="تحصیلی" className="bg-[#1a1a1a] text-[#4ADE80] py-2">🎓 تحصیلی</option>
                <option value="کاری" className="bg-[#1a1a1a] text-[#60A5FA] py-2">💼 کاری</option>
                <option value="توریستی" className="bg-[#1a1a1a] text-[#F59E0B] py-2">✈️ توریستی</option>
                <option value="اقامتی" className="bg-[#1a1a1a] text-[#EC4899] py-2">🏡 اقامتی</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-300 mb-2">آخرین مدرک تحصیلی</label>
              <select
                name="education_level"
                data-testid="education-level-input"
                value={formData.education_level}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 cursor-pointer"
              >
                <option value="" className="bg-[#1a1a1a] text-zinc-400">انتخاب کنید...</option>
                <option value="دیپلم" className="bg-[#1a1a1a] text-[#A78BFA] py-2">📚 دیپلم</option>
                <option value="کاردانی" className="bg-[#1a1a1a] text-[#34D399] py-2">📖 کاردانی</option>
                <option value="کارشناسی" className="bg-[#1a1a1a] text-[#60A5FA] py-2">🎓 کارشناسی</option>
                <option value="کارشناسی ارشد" className="bg-[#1a1a1a] text-[#F59E0B] py-2">👨‍🎓 کارشناسی ارشد</option>
                <option value="دکتری" className="bg-[#1a1a1a] text-[#D4AF37] py-2">🏆 دکتری</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2">توضیحات بیشتر</label>
            <textarea
              name="description"
              rows="4"
              data-testid="description-textarea"
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] resize-none"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-button"
            className="w-full px-6 py-4 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? 'در حال ارسال...' : 'ارسال درخواست'}
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactPage;